import React, { useMemo, useRef, useState } from "react";
import { formatBRL, fileUrl } from "../lib/api";
import { processAndUploadImage } from "../lib/imageUpload";
import { toast } from "sonner";
import {
  Gear, CaretDown, Plus, Trash, Stack, PuzzlePiece, ShieldCheck,
  Gift, Warning, Calculator, Camera, X, Info, Package, CaretRight,
} from "@phosphor-icons/react";

const KIND_META = {
  variant: { label: "Variante", hint: "Escolha única — altera o valor base (ex: tipo de entulho)", color: "#7C3AED" },
  addon: { label: "Opcional / Acessório", hint: "Múltipla escolha — soma ao valor (ex: pé com rodinha)", color: "#004E98" },
  consumable: { label: "Consumível (venda)", hint: "Item que não retorna (ex: shampoo, lâmina)", color: "#FF6B35" },
};

const CAUCAO_FEE = 5.0;

/** Miniatura + upload de foto (opcional). Reaproveita processAndUploadImage. */
function ThumbPicker({ value, onChange, testid, size = 48 }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const { path } = await processAndUploadImage(f);
      onChange(path);
    } catch (err) {
      toast.error(err?.message || "Falha ao enviar a foto.");
    } finally {
      setBusy(false);
    }
  };
  const wh = { width: size, height: size };
  return (
    <div className="relative shrink-0" style={wh}>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      {value ? (
        <>
          <button type="button" onClick={() => ref.current?.click()}
            className="block w-full h-full rounded-lg overflow-hidden border-2 border-slate-200 hover:border-[#004E98] transition"
            data-testid={testid} title="Trocar foto">
            <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
            data-testid={`${testid}-clear`} title="Remover foto">
            <X size={9} weight="bold" />
          </button>
        </>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className="w-full h-full rounded-lg border-2 border-dashed border-slate-300 hover:border-[#004E98] hover:bg-slate-50 transition flex flex-col items-center justify-center text-slate-400 disabled:opacity-50"
          data-testid={testid} title="Adicionar foto">
          {busy ? <div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                : <Camera size={size >= 56 ? 20 : 14} weight="bold" />}
        </button>
      )}
    </div>
  );
}

/** V17.9 — Dropdown customizado com miniatura para escolher um anúncio (combos). */
function ListingThumbSelect({ value, onChange, listings, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => listings.find((l) => String(l.id) === String(value)), [value, listings]);
  return (
    <div className="relative" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full aj-input flex items-center gap-2 text-left"
        data-testid={`${testid}-btn`}>
        {selected ? (
          <>
            <div className="w-8 h-8 rounded-md overflow-hidden bg-slate-100 shrink-0">
              {selected.image ? <img src={fileUrl(selected.image)} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-xs text-slate-800 truncate">{selected.title}</div>
              <div className="text-[10px] text-slate-500">{formatBRL(selected.price_per_day || 0)}</div>
            </div>
          </>
        ) : (
          <span className="text-slate-400 text-sm flex-1">{placeholder || "Selecione um anúncio…"}</span>
        )}
        <CaretDown size={14} weight="bold" className="text-slate-400 shrink-0" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[80]" onClick={(e) => e.target === e.currentTarget && setOpen(false)} />
          <div className="absolute z-[90] top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg border-2 border-slate-200 bg-white shadow-xl" data-testid={`${testid}-menu`}>
            {listings.length === 0 && (
              <div className="p-3 text-[11px] text-slate-500">Nenhum outro anúncio ativo disponível para combo.</div>
            )}
            {listings.map((l) => (
              <button key={l.id} type="button" onClick={() => { onChange(l.id); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-2.5 py-2 hover:bg-slate-50 text-left ${String(l.id) === String(value) ? "bg-emerald-50" : ""}`}
                data-testid={`${testid}-opt-${l.id}`}>
                <div className="w-9 h-9 rounded-md overflow-hidden bg-slate-100 shrink-0">
                  {l.image ? <img src={fileUrl(l.image)} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-xs text-slate-800 truncate">{l.title}</div>
                  <div className="text-[10px] text-slate-500">{formatBRL(l.price_per_day || 0)}</div>
                </div>
                {String(l.id) === String(value) && <span className="text-[10px] font-bold text-emerald-700">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function AdvancedListingConfig({ form, setForm, ownListings = [], unitWord = "dia" }) {
  const [open, setOpen] = useState(
    () => !!(form.caucao_active || (form.complements || []).length || (form.combos || []).length ||
      parseInt(form.qty_min || 1, 10) > 1 || parseInt(form.qty_step || 1, 10) > 1 || form.conv_active)
  );
  const [tab, setTab] = useState("qty"); // qty | variants | addons | consumables | caucao | combos

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  const comps = useMemo(() => form.complements || [], [form.complements]);
  const combos = useMemo(() => form.combos || [], [form.combos]);
  const price = parseFloat(form.price_per_day || 0) || 0;
  const caucaoCap = price * 10;

  const updComp = (i, patch) => upd({ complements: comps.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const removeComp = (i) => upd({ complements: comps.filter((_, idx) => idx !== i) });
  const addComp = (kind) => upd({
    complements: [...comps, {
      kind, name: "", required: false, charge: "per_rental", price: "", max_qty: "1",
      image: "",
      stock_qty: "", paused: false,
      require_active: false, require_mode: "optional",
      require_min_fixed: "1",
      require_per_source: "quantity", require_per_base_qty: "2", require_ratio: "1",
      options: kind === "variant"
        ? [{ label: "", price_delta: "0", image: "", is_default: true }, { label: "", price_delta: "", image: "", is_default: false }]
        : [],
    }],
  });

  const configuredBadges = [];
  if (parseInt(form.qty_min || 1, 10) > 1 || parseInt(form.qty_step || 1, 10) > 1 || form.conv_active) configuredBadges.push("Quantidade");
  const nVariant = comps.filter((c) => c.kind === "variant").length;
  const nAddon = comps.filter((c) => c.kind === "addon").length;
  const nConsum = comps.filter((c) => c.kind === "consumable").length;
  if (nVariant) configuredBadges.push(`${nVariant} variante(s)`);
  if (nAddon) configuredBadges.push(`${nAddon} opcional(is)`);
  if (nConsum) configuredBadges.push(`${nConsum} consumível(is)`);
  if (form.caucao_active) configuredBadges.push("Caução");
  if (combos.length) configuredBadges.push(`${combos.length} combo(s)`);

  const TABS = [
    { key: "qty", label: "Quantidade", icon: Stack, count: (parseInt(form.qty_min || 1, 10) > 1 || parseInt(form.qty_step || 1, 10) > 1 || form.conv_active) ? "•" : 0 },
    { key: "variants", label: "Variantes", icon: PuzzlePiece, count: nVariant },
    { key: "addons", label: "Opcionais", icon: Package, count: nAddon },
    { key: "consumables", label: "Consumíveis", icon: Gift, count: nConsum },
    { key: "caucao", label: "Caução", icon: ShieldCheck, count: form.caucao_active ? "•" : 0 },
    { key: "combos", label: "Combos", icon: Gift, count: combos.length },
  ];

  // Retorna a lista de anúncios elegíveis para combo (exceto o próprio e sem duplicar)
  const availableComboListings = useMemo(() => {
    const used = new Set(combos.map((c) => String(c.listing_id)).filter(Boolean));
    return (ownListings || []).map((l) => ({
      id: l.id, title: l.title,
      image: (l.thumbnails || l.images || [null])[0],
      price_per_day: l.price_per_day,
      _used: used.has(String(l.id)),
    }));
  }, [ownListings, combos]);

  return (
    <div className="rounded-2xl border-2 border-slate-200 overflow-hidden" data-testid="advanced-config-block">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid="advanced-config-toggle">
        <span className="flex items-center gap-2 text-sm font-black text-slate-700">
          <Gear size={18} weight="bold" className="text-[#004E98]" /> Configurações avançadas
          <span className="text-[10px] font-bold text-slate-400 hidden sm:inline">(opcional)</span>
        </span>
        <span className="flex items-center gap-2">
          {open ? null : configuredBadges.length > 0 && (
            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="advanced-config-badges">
              {configuredBadges.join(" · ")}
            </span>
          )}
          <CaretDown size={16} weight="bold" className={`text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="bg-slate-50/50" data-testid="advanced-config-body">
          {/* V17.9 — Abas sticky no topo */}
          <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b-2 border-slate-200 overflow-x-auto">
            <div className="flex" data-testid="adv-tabs">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => setTab(t.key)}
                    className={`px-3 py-2.5 text-[11px] font-black uppercase tracking-wider whitespace-nowrap border-b-2 transition inline-flex items-center gap-1.5 ${active ? "border-[#004E98] text-[#004E98]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                    data-testid={`adv-tab-${t.key}`}>
                    <Icon size={13} weight="bold" />
                    {t.label}
                    {!!t.count && t.count !== 0 && (
                      <span className={`text-[9px] rounded-full min-w-[16px] h-4 px-1 inline-flex items-center justify-center ${active ? "bg-[#004E98] text-white" : "bg-slate-200 text-slate-700"}`}>{t.count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-3 space-y-3">
            {tab === "qty" && (
              <QtyRulesTab form={form} upd={upd} />
            )}
            {tab === "variants" && (
              <ComplementsTab kind="variant" comps={comps} addComp={addComp} updComp={updComp} removeComp={removeComp} price={price} unitWord={unitWord} form={form} setForm={setForm} />
            )}
            {tab === "addons" && (
              <ComplementsTab kind="addon" comps={comps} addComp={addComp} updComp={updComp} removeComp={removeComp} price={price} unitWord={unitWord} form={form} setForm={setForm} />
            )}
            {tab === "consumables" && (
              <ComplementsTab kind="consumable" comps={comps} addComp={addComp} updComp={updComp} removeComp={removeComp} price={price} unitWord={unitWord} form={form} setForm={setForm} />
            )}
            {tab === "caucao" && (
              <CaucaoTab form={form} upd={upd} price={price} caucaoCap={caucaoCap} unitWord={unitWord} />
            )}
            {tab === "combos" && (
              <CombosTab combos={combos} upd={upd} listings={availableComboListings} form={form} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Tabs

function QtyRulesTab({ form, upd }) {
  const convFactor = parseFloat(form.conv_factor || 0) || 0;
  const convUnit = (form.conv_unit || "unidade").trim() || "unidade";
  const exampleN = 3;
  const examplePieces = convFactor > 0 ? Math.ceil(exampleN * convFactor) : 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="adv-qty-section">
      <div className="text-xs font-black text-slate-800 mb-1">Mínimo por pedido, incremento (lote) e calculadora por medida</div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Quantidade mínima</span>
          <input type="number" min="1" step="1" placeholder="1" value={form.qty_min}
            onChange={(e) => upd({ qty_min: e.target.value })} className="aj-input mt-1" data-testid="adv-qty-min" />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Incremento (lote)</span>
          <input type="number" min="1" step="1" placeholder="1" value={form.qty_step}
            onChange={(e) => upd({ qty_step: e.target.value })} className="aj-input mt-1" data-testid="adv-qty-step" />
        </label>
      </div>
      {(parseInt(form.qty_min || 1, 10) > 1 || parseInt(form.qty_step || 1, 10) > 1) && (
        <div className="text-[10px] text-slate-500 bg-slate-50 rounded-lg p-1.5">
          Ex.: mínimo {Math.max(1, parseInt(form.qty_min || 1, 10) || 1)}, depois de {Math.max(1, parseInt(form.qty_step || 1, 10) || 1)} em {Math.max(1, parseInt(form.qty_step || 1, 10) || 1)} unidade(s).
        </div>
      )}

      <label className="flex items-start gap-2 cursor-pointer pt-2 border-t border-slate-100 mt-2">
        <input type="checkbox" checked={!!form.conv_active} onChange={(e) => upd({ conv_active: e.target.checked })}
          className="w-4 h-4 accent-[#004E98] mt-0.5" data-testid="adv-conv-active" />
        <span className="flex-1">
          <span className="text-xs font-bold text-slate-700 flex items-center gap-1">
            <Calculator size={14} weight="bold" className="text-[#004E98]" /> Ativar calculadora por medida
          </span>
          <span className="text-[10px] text-slate-500 block mt-0.5">
            O cliente informa uma medida (ex: <b>altura em metros</b>) e o app calcula <b>quantas peças</b> ele precisa alugar.
          </span>
        </span>
      </label>

      {form.conv_active && (
        <div className="space-y-2 rounded-lg border border-[#004E98]/25 bg-[#004E98]/[0.04] p-2.5" data-testid="adv-conv-block">
          <div className="text-[10px] font-black uppercase tracking-wider text-[#004E98] flex items-center gap-1">
            <Info size={11} weight="fill" /> Como funciona
          </div>
          <div className="text-[10px] text-slate-600 leading-relaxed bg-white/60 rounded p-1.5 border border-white">
            <b>1.</b> Você define a <b>pergunta</b> (ex: &quot;Quantos metros de altura?&quot;).
            <b> 2.</b> Define a <b>unidade</b> e <b>quantas peças por unidade</b>.
            <b> 3.</b> No aluguel, o cliente digita a medida e o app calcula a quantidade.
          </div>
          <label className="block">
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Pergunta ao cliente</span>
            <input placeholder="Ex: Quantos metros de altura você precisa?" value={form.conv_question}
              onChange={(e) => upd({ conv_question: e.target.value })} className="aj-input mt-1" data-testid="adv-conv-question" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Unidade que o cliente digita</span>
              <input placeholder="metro" value={form.conv_unit} onChange={(e) => upd({ conv_unit: e.target.value })}
                className="aj-input mt-1" data-testid="adv-conv-unit" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Peças por {form.conv_unit ? form.conv_unit.trim() : "unidade"}</span>
              <input type="number" min="0.1" step="0.1" placeholder="2" value={form.conv_factor}
                onChange={(e) => upd({ conv_factor: e.target.value })} className="aj-input mt-1" data-testid="adv-conv-factor" />
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Uso da calculadora</span>
            <select value={form.conv_required ? "obrigatorio" : "opcional"} onChange={(e) => upd({ conv_required: e.target.value === "obrigatorio" })}
              className="aj-input mt-1" data-testid="adv-conv-required">
              <option value="opcional">Opcional — o cliente pode digitar a quantidade direto</option>
              <option value="obrigatorio">Obrigatório — a quantidade vem sempre da calculadora</option>
            </select>
          </label>
          {convFactor > 0 && convUnit && (
            <div className="text-[10px] text-emerald-800 bg-emerald-50 rounded-lg p-2 border border-emerald-200 leading-relaxed" data-testid="adv-conv-preview">
              <b>Prévia:</b> se o cliente digitar <b>{exampleN} {convUnit}{exampleN > 1 && !convUnit.endsWith("s") ? "s" : ""}</b>,
              o app calcula <b>{examplePieces} peça(s)</b> a alugar.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ComplementsTab({ kind, comps, addComp, updComp, removeComp, price, unitWord, form, setForm }) {
  const meta = KIND_META[kind];
  // Índices reais no array `comps` só do kind atual
  const items = comps.map((c, idx) => ({ c, idx })).filter((x) => x.c.kind === kind);

  // V17.10.1 — Grupos como cidadãos de primeira classe (apenas em ADDON).
  // Merge de duas fontes: (a) grupos derivados de comps[].group_key; (b) `form.addon_groups`
  // — grupos criados pelo dono ANTES de atribuir opcionais (fica persistido no form).
  const groups = React.useMemo(() => {
    if (kind !== "addon") return [];
    const map = new Map();
    // Fonte (b): grupos "vazios" que o dono criou primeiro
    ((form?.addon_groups) || []).forEach((g) => {
      if (!g?.key) return;
      map.set(g.key, { key: g.key, mode: g.mode || "exclusive", max: g.max || 2, count: 0 });
    });
    // Fonte (a): grupos derivados de opcionais existentes (sobrescreve com contagem real)
    (comps || []).forEach((c) => {
      if (c.kind !== "addon" || !(c.group_key || "").trim()) return;
      const k = c.group_key.trim();
      if (!map.has(k)) {
        map.set(k, { key: k, mode: c.group_mode || "exclusive", max: c.group_max || 2, count: 1 });
      } else {
        const cur = map.get(k);
        cur.count += 1;
        // Sincroniza mode/max com o primeiro addon do grupo (fonte-de-verdade quando há divergência)
        if (!cur.mode) cur.mode = c.group_mode || "exclusive";
        if (!cur.max && c.group_max) cur.max = c.group_max;
      }
    });
    return Array.from(map.values());
  }, [comps, kind, form?.addon_groups]);

  const [groupModal, setGroupModal] = useState(null); // { editingKey: string | null, name, mode, max }
  const openNewGroup = () => setGroupModal({ editingKey: null, name: "", mode: "exclusive", max: 2 });
  const openEditGroup = (g) => setGroupModal({ editingKey: g.key, name: g.key, mode: g.mode, max: g.max });
  const saveGroup = () => {
    const g = groupModal;
    const nm = (g.name || "").trim();
    if (!nm) { alert("Dê um nome ao grupo."); return; }
    const mode = g.mode === "max" ? "max" : "exclusive";
    const max = mode === "max" ? Math.max(1, parseInt(g.max || 2, 10) || 2) : null;
    const oldKey = g.editingKey;
    // Atualiza os complementos já atribuídos
    const nextComps = (comps || []).map((c) => {
      if (c.kind !== "addon") return c;
      if (oldKey && c.group_key === oldKey) return { ...c, group_key: nm, group_mode: mode, group_max: max };
      if (!oldKey && c.group_key === nm) return { ...c, group_mode: mode, group_max: max };
      return c;
    });
    // Atualiza a lista de grupos persistente (fonte-b)
    const prevGroups = (form?.addon_groups || []).filter((x) => x && x.key);
    let nextGroups;
    if (oldKey) {
      // Rename: substitui a entrada antiga
      nextGroups = prevGroups.map((x) => x.key === oldKey ? { key: nm, mode, max } : x);
      // Se o oldKey não estava lá (grupo era só derivado), adiciona a nova
      if (!nextGroups.some((x) => x.key === nm)) nextGroups.push({ key: nm, mode, max });
    } else {
      // Novo: adiciona se ainda não existir
      nextGroups = prevGroups.some((x) => x.key === nm) ? prevGroups.map((x) => x.key === nm ? { key: nm, mode, max } : x) : [...prevGroups, { key: nm, mode, max }];
    }
    setForm({ ...form, complements: nextComps, addon_groups: nextGroups });
    setGroupModal(null);
  };
  const deleteGroup = (key) => {
    if (!window.confirm(`Excluir o grupo "${key}"? Os opcionais dele voltam a ficar sem grupo.`)) return;
    const nextComps = (comps || []).map((c) => (c.kind === "addon" && c.group_key === key) ? { ...c, group_key: null, group_mode: null, group_max: null } : c);
    const nextGroups = (form?.addon_groups || []).filter((x) => x.key !== key);
    setForm({ ...form, complements: nextComps, addon_groups: nextGroups });
  };

  return (
    <div className="space-y-2" data-testid={`adv-tab-content-${kind}`}>
      <div className="rounded-lg border-2 p-2.5 text-[11px]"
        style={{ borderColor: `${meta.color}44`, background: `${meta.color}0A` }}>
        <b style={{ color: meta.color }}>{meta.label}:</b> <span className="text-slate-700">{meta.hint}</span>
      </div>

      {/* V17.10.1 — Seção de Grupos (só p/ opcionais). Fica no TOPO para o dono criar antes de cadastrar. */}
      {kind === "addon" && (
        <div className="rounded-lg border-2 border-purple-200 bg-purple-50/50 p-2.5 space-y-2" data-testid="adv-groups-section">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-black uppercase tracking-wider text-purple-800">Grupos de opcionais</div>
              <div className="text-[10px] text-purple-700/80">Organize opcionais em grupos com regras de seleção (ex.: só 1 dessas cores).</div>
            </div>
            <button type="button" onClick={openNewGroup}
              className="text-[11px] font-bold rounded-full border-2 border-purple-400 text-purple-700 px-2.5 py-1 hover:bg-purple-100 inline-flex items-center gap-1"
              data-testid="adv-group-new">
              <Plus size={12} weight="bold" /> Novo grupo
            </button>
          </div>
          {groups.length === 0 && (
            <div className="text-[10px] text-purple-600/70 text-center py-1">Nenhum grupo — os opcionais serão independentes.</div>
          )}
          {groups.map((g) => (
            <div key={g.key} className="bg-white rounded-md border border-purple-200 px-2.5 py-1.5 flex items-center gap-2" data-testid={`adv-group-${g.key}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-slate-800 truncate">{g.key}</div>
                <div className="text-[10px] text-slate-500">
                  {g.mode === "exclusive" ? "Escolha 1" : `Máximo ${g.max}`} · {g.count} {g.count === 1 ? "opcional" : "opcionais"}
                </div>
              </div>
              <button type="button" onClick={() => openEditGroup(g)} className="text-[10px] font-bold text-purple-700 border border-purple-300 rounded-md px-2 py-1 hover:bg-purple-50" data-testid={`adv-group-edit-${g.key}`}>Editar</button>
              <button type="button" onClick={() => deleteGroup(g.key)} className="text-[10px] font-bold text-red-600 border border-red-300 rounded-md px-2 py-1 hover:bg-red-50" data-testid={`adv-group-delete-${g.key}`}>×</button>
            </div>
          ))}
        </div>
      )}

      {items.map(({ c, idx: i }) => (
        <ComplementCard key={i} c={c} i={i} kind={kind} meta={meta}
          updComp={updComp} removeComp={removeComp} price={price} unitWord={unitWord} form={form}
          availableGroups={groups} />
      ))}

      <button type="button" onClick={() => addComp(kind)}
        className="text-[11px] font-bold rounded-full border-2 px-3 py-2 inline-flex items-center gap-1 hover:bg-slate-50 transition w-full justify-center"
        style={{ borderColor: `${meta.color}55`, color: meta.color }}
        data-testid={`adv-add-${kind}`}>
        <Plus size={14} weight="bold" /> Adicionar {meta.label.toLowerCase()}
      </button>

      {items.length === 0 && (
        <div className="text-[11px] text-slate-400 text-center py-4">
          {kind === "variant" && "Ex.: tipo de entulho, tamanho de container. Escolha ÚNICA que altera o preço-base."}
          {kind === "addon" && "Ex.: pé com rodinha, tela de proteção. Adiciona ao preço."}
          {kind === "consumable" && "Ex.: shampoo, lâmina. Item vendido (não retorna)."}
        </div>
      )}

      {/* V17.10.1 — Modal de criar/editar grupo */}
      {groupModal && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setGroupModal(null)} data-testid="adv-group-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-bold">{groupModal.editingKey ? "Editar grupo" : "Novo grupo"}</div>
            <label className="block">
              <span className="text-[10px] uppercase font-bold text-slate-500">Nome do grupo</span>
              <input value={groupModal.name} onChange={(e) => setGroupModal((s) => ({ ...s, name: e.target.value }))}
                placeholder="Ex.: cor, tamanho, travantes" className="aj-input" data-testid="adv-group-name" autoFocus />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase font-bold text-slate-500">Regra de seleção</span>
              <select value={groupModal.mode} onChange={(e) => setGroupModal((s) => ({ ...s, mode: e.target.value }))}
                className="aj-input" data-testid="adv-group-mode">
                <option value="exclusive">Exclusivo — só 1 pode ser escolhido</option>
                <option value="max">Máximo N — ao passar do limite, remove o mais antigo</option>
              </select>
            </label>
            {groupModal.mode === "max" && (
              <label className="block">
                <span className="text-[10px] uppercase font-bold text-slate-500">Máximo de seleções</span>
                <input type="number" min="1" max="99" value={groupModal.max} onChange={(e) => setGroupModal((s) => ({ ...s, max: Math.max(1, parseInt(e.target.value) || 1) }))}
                  className="aj-input" data-testid="adv-group-max" />
              </label>
            )}
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setGroupModal(null)} className="btn-ghost flex-1" data-testid="adv-group-cancel">Cancelar</button>
              <button type="button" onClick={saveGroup} className="btn-primary flex-1" data-testid="adv-group-save">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ComplementCard({ c, i, kind, meta, updComp, removeComp, price, unitWord, form, availableGroups }) {
  return (
    <div className="rounded-lg border border-slate-200 p-2.5 space-y-2 bg-white" data-testid={`adv-comp-${i}`}>
      <div className="flex items-center justify-between">
        <span className="text-[9px] uppercase font-black tracking-wider px-2 py-0.5 rounded-full text-white" style={{ background: meta.color }}>
          {meta.label}
        </span>
        <button type="button" onClick={() => removeComp(i)} className="text-red-500 hover:text-red-700 transition" data-testid={`adv-comp-remove-${i}`}>
          <Trash size={14} weight="bold" />
        </button>
      </div>

      {kind === "variant" ? (
        <input placeholder="Nome da variante (ex: Tipo de entulho)" value={c.name || ""}
          onChange={(e) => updComp(i, { name: e.target.value })} className="aj-input font-bold" data-testid={`adv-comp-name-${i}`} />
      ) : (
        <div className="flex items-start gap-2">
          <ThumbPicker value={c.image || ""} onChange={(v) => updComp(i, { image: v })} testid={`adv-comp-image-${i}`} size={56} />
          <input placeholder={kind === "consumable" ? "Nome do consumível (ex: Shampoo 100ml)" : "Nome do opcional (ex: Pé com rodinha)"}
            value={c.name || ""} onChange={(e) => updComp(i, { name: e.target.value })}
            className="aj-input flex-1 font-bold" data-testid={`adv-comp-name-${i}`} />
        </div>
      )}

      {/* V18.8 — Descrição opcional (mostrada colapsada no checkout ao selecionar) */}
      <textarea
        rows={2}
        maxLength={280}
        value={c.description || ""}
        onChange={(e) => updComp(i, { description: e.target.value })}
        placeholder={kind === "consumable" ? "Descrição (opcional) — ex: uso indicado, orientação de aplicação..." : "Descrição (opcional) — ex: benefícios, medidas, cuidados..."}
        className="aj-input py-1.5 text-[11px] w-full resize-none"
        data-testid={`adv-comp-description-${i}`}
      />

      {kind === "variant" ? (
        <VariantOptions c={c} i={i} updComp={updComp} price={price} unitWord={unitWord} />
      ) : (
        <AddonConsumableFields c={c} i={i} kind={kind} updComp={updComp} unitWord={unitWord} form={form} availableGroups={availableGroups} />
      )}
    </div>
  );
}

function VariantOptions({ c, i, updComp, price, unitWord }) {
  const setDefaultAt = (idx) => updComp(i, {
    options: c.options.map((x, xi) => xi === idx ? { ...x, is_default: true, price_delta: 0 } : { ...x, is_default: false }),
  });
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!c.required} onChange={(e) => updComp(i, { required: e.target.checked })}
          className="w-3.5 h-3.5 accent-[#7C3AED]" data-testid={`adv-comp-required-${i}`} />
        <span className="text-[11px] font-bold text-slate-600">Escolha obrigatória no aluguel</span>
      </label>
      <div className="space-y-1.5">
        {(c.options || []).map((o, oi) => (
          <div key={oi} className={`rounded-lg border-2 ${o.is_default ? "border-[#7C3AED] bg-[#7C3AED]/[0.04]" : "border-slate-200 bg-slate-50/50"} p-1.5 transition`} data-testid={`adv-comp-${i}-opt-${oi}`}>
            <div className="flex items-start gap-1.5">
              <ThumbPicker value={o.image || ""} onChange={(v) => updComp(i, { options: c.options.map((x, xi) => xi === oi ? { ...x, image: v } : x) })}
                testid={`adv-comp-${i}-opt-image-${oi}`} size={44} />
              <div className="flex-1 min-w-0 space-y-1">
                <input placeholder={`Opção ${oi + 1} — ex: Entulho limpo`} value={o.label || ""}
                  onChange={(e) => updComp(i, { options: c.options.map((x, xi) => xi === oi ? { ...x, label: e.target.value } : x) })}
                  className="aj-input py-2 text-sm" data-testid={`adv-comp-${i}-opt-label-${oi}`} />
                <div className="flex items-center gap-1.5">
                  {o.is_default ? (
                    <div className="flex-1 rounded-lg border-2 border-dashed border-[#7C3AED]/40 bg-[#7C3AED]/5 px-2.5 py-2 flex items-center gap-1.5" data-testid={`adv-comp-${i}-opt-base-${oi}`}>
                      <span className="text-[9px] font-black text-[#7C3AED] uppercase tracking-wider">Preço base</span>
                      <span className="text-sm font-black text-slate-800">{price > 0 ? formatBRL(price) : `R$ 0,00`}</span>
                      <span className="text-[10px] text-slate-500">/{unitWord}</span>
                    </div>
                  ) : (
                    <div className="relative flex-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] font-black text-emerald-600 pointer-events-none">+R$</span>
                      <input type="number" step="0.01" min="0" placeholder="0,00" title={`Adicional sobre o preço-base por ${unitWord}`}
                        value={o.price_delta}
                        onChange={(e) => updComp(i, { options: c.options.map((x, xi) => xi === oi ? { ...x, price_delta: e.target.value } : x) })}
                        className="aj-input py-2 text-sm pl-10" data-testid={`adv-comp-${i}-opt-delta-${oi}`} />
                    </div>
                  )}
                  <label className="flex items-center gap-1 cursor-pointer shrink-0 px-2 py-1.5 rounded-md hover:bg-white/60 transition" title="Marcar como opção padrão">
                    <input type="radio" name={`comp-${i}-default`} checked={!!o.is_default} onChange={() => setDefaultAt(oi)}
                      className="w-3.5 h-3.5 accent-[#7C3AED]" data-testid={`adv-comp-${i}-opt-default-${oi}`} />
                    <span className="text-[10px] font-bold text-slate-600">padrão</span>
                  </label>
                </div>
              </div>
              {(c.options || []).length > 2 && (
                <button type="button" onClick={() => updComp(i, { options: c.options.filter((_, xi) => xi !== oi) })}
                  className="text-slate-400 hover:text-red-500 transition shrink-0 self-start p-1"
                  data-testid={`adv-comp-${i}-opt-remove-${oi}`}>
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
            {/* V18.3 — Descrição opcional. Se preenchida, aparece colapsável no checkout quando esta opção é selecionada */}
            <textarea
              rows={2}
              placeholder="Descrição (opcional) — ex: pode ser jogado madeira, pedra, tijolo..."
              value={o.description || ""}
              onChange={(e) => updComp(i, { options: c.options.map((x, xi) => xi === oi ? { ...x, description: e.target.value } : x) })}
              className="aj-input py-1.5 text-[11px] w-full mt-1.5 resize-none"
              maxLength={280}
              data-testid={`adv-comp-${i}-opt-description-${oi}`}
            />
          </div>
        ))}
      </div>
      <button type="button" onClick={() => updComp(i, { options: [...(c.options || []), { label: "", price_delta: "", image: "", is_default: false, description: "" }] })}
        className="text-[11px] font-bold text-[#7C3AED] inline-flex items-center gap-1" data-testid={`adv-comp-${i}-add-opt`}>
        <Plus size={12} weight="bold" /> Adicionar opção
      </button>
      <div className="text-[10px] text-slate-400 leading-relaxed">
        A opção <b>padrão</b> usa o próprio valor do aluguel ({price > 0 ? formatBRL(price) : "R$ 0,00"}/{unitWord}).
        As demais somam <b>+R$</b> ao preço-base (nunca subtraem).
      </div>
    </div>
  );
}

function AddonConsumableFields({ c, i, kind, updComp, unitWord, form, availableGroups }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="block">
        <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Preço (R$)</span>
        <div className="relative mt-1">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-400 pointer-events-none">R$</span>
          <input type="number" step="0.01" min="0" placeholder="10,00" value={c.price}
            onChange={(e) => updComp(i, { price: e.target.value })} className="aj-input pl-8" data-testid={`adv-comp-price-${i}`} />
        </div>
      </label>
      <label className="block">
        <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Qtd. máxima</span>
        <input type="number" step="1" min="1" placeholder="1" value={c.max_qty}
          onChange={(e) => updComp(i, { max_qty: e.target.value })} className="aj-input mt-1" data-testid={`adv-comp-maxqty-${i}`} />
      </label>
      {kind === "addon" && (
        <label className="block col-span-2">
          <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Cobrança</span>
          <select value={c.charge || "per_rental"} onChange={(e) => updComp(i, { charge: e.target.value })}
            className="aj-input mt-1" data-testid={`adv-comp-charge-${i}`}>
            <option value="per_rental">Uma vez por locação</option>
            <option value="per_cycle">Por {unitWord} (acompanha a duração)</option>
          </select>
        </label>
      )}
      {kind === "consumable" && (
        <div className="col-span-2 text-[10px] text-slate-400">Consumível é vendido (não retorna) — cobrado uma vez.</div>
      )}
      <div className={`col-span-2 border-t border-slate-100 pt-2 space-y-2 ${c.paused ? "opacity-70" : ""}`} data-testid={`adv-comp-stock-${i}`}>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className={`text-[10px] uppercase font-bold tracking-wider ${parseInt(c.stock_qty || 0, 10) > 0 && parseInt(c.stock_qty, 10) <= 1 ? "text-red-600" : "text-slate-500"}`}>
              {kind === "consumable" ? "Estoque disponível" : "Unidades disponíveis"}
              {parseInt(c.stock_qty || 0, 10) > 0 && parseInt(c.stock_qty, 10) <= 1 && " ⚠ baixo"}
            </span>
            <input type="number" step="1" min="0" placeholder="Deixe vazio p/ ilimitado" value={c.stock_qty ?? ""}
              onChange={(e) => updComp(i, { stock_qty: e.target.value })}
              className={`aj-input mt-1 ${parseInt(c.stock_qty || 0, 10) > 0 && parseInt(c.stock_qty, 10) <= 1 ? "!border-red-400 !bg-red-50/50" : ""}`}
              data-testid={`adv-comp-stock-qty-${i}`} />
          </label>
          <label className="flex items-end gap-2 pb-1 cursor-pointer">
            <input type="checkbox" checked={!!c.paused} onChange={(e) => updComp(i, { paused: e.target.checked })}
              className="w-4 h-4 accent-amber-500 mb-1" data-testid={`adv-comp-paused-${i}`} />
            <span className="text-[11px] font-bold text-slate-700 mb-1">
              Pausar este {kind === "consumable" ? "consumível" : "opcional"}
              <span className="block text-[10px] font-normal text-slate-500">Some do checkout do cliente sem excluir.</span>
            </span>
          </label>
        </div>
      </div>
      {kind === "addon" && (
        <div className="col-span-2 border-t border-slate-100 pt-2 space-y-2">
          <label className="block">
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Obrigatoriedade</span>
            <select value={c.require_mode || (c.require_active ? "proportional" : "optional")}
              onChange={(e) => {
                const m = e.target.value;
                updComp(i, { require_mode: m, require_active: m !== "optional" });
              }}
              className="aj-input mt-1" data-testid={`adv-comp-require-mode-${i}`}>
              <option value="optional">Opcional — o cliente escolhe se quer</option>
              <option value="fixed">Obrigatório — mínimo fixo (cliente pode aumentar)</option>
              <option value="proportional">Obrigatório em proporção ao item (ex: 1 trava a cada 2m)</option>
            </select>
          </label>

          {(c.require_mode === "fixed") && (
            <div className="rounded-lg border border-[#004E98]/25 bg-[#004E98]/[0.04] p-2 space-y-1.5" data-testid={`adv-comp-require-fixed-block-${i}`}>
              <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-slate-700">
                <span>Mínimo obrigatório de</span>
                <input type="number" min="1" step="1" value={c.require_min_fixed || "1"}
                  onChange={(e) => updComp(i, { require_min_fixed: e.target.value })}
                  className="aj-input py-1 px-2 text-xs w-14 text-center"
                  data-testid={`adv-comp-require-min-fixed-${i}`} />
                <span>unidade(s) — cliente pode aumentar até o máximo</span>
              </div>
            </div>
          )}

          {(c.require_mode === "proportional") && (
            <div className="rounded-lg border border-[#004E98]/25 bg-[#004E98]/[0.04] p-2 space-y-1.5" data-testid={`adv-comp-require-block-${i}`}>
              <div className="flex items-center gap-1 flex-wrap text-[11px] text-slate-700">
                <span>Obrigatórias</span>
                <input type="number" min="1" step="1" value={c.require_ratio || "1"}
                  onChange={(e) => updComp(i, { require_ratio: e.target.value })}
                  className="aj-input py-1 px-2 text-xs w-14 text-center"
                  data-testid={`adv-comp-require-ratio-${i}`} />
                <span>unidade(s) desse opcional</span>
                <span className="w-full block text-[11px] text-slate-700 mt-1">a cada</span>
                <input type="number" min="1" step="1" value={c.require_per_base_qty || "2"}
                  onChange={(e) => updComp(i, { require_per_base_qty: e.target.value })}
                  className="aj-input py-1 px-2 text-xs w-14 text-center"
                  data-testid={`adv-comp-require-per-${i}`} />
                <select value={c.require_per_source || "quantity"}
                  onChange={(e) => updComp(i, { require_per_source: e.target.value })}
                  className="aj-input py-1 text-xs w-auto" data-testid={`adv-comp-require-source-${i}`}>
                  <option value="quantity">unidade(s) alugada(s)</option>
                  {form.conv_active && (
                    <option value="measure">{form.conv_unit ? `${form.conv_unit}(s) — calculadora` : "medida — calculadora"}</option>
                  )}
                </select>
              </div>
            </div>
          )}

          {/* V17.10.1 — Grupo (dropdown compacto). Modo/max são configurados no topo da aba. */}
          {kind === "addon" && (availableGroups?.length > 0 || c.group_key) && (
            <div className="border-t border-slate-100 pt-2 mt-1" data-testid={`adv-comp-group-block-${i}`}>
              <label className="flex items-center gap-2">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 shrink-0">Grupo</span>
                <select
                  value={c.group_key || ""}
                  onChange={(e) => {
                    const key = e.target.value || null;
                    // Ao entrar num grupo existente, herda mode/max desse grupo
                    if (key) {
                      const g = (availableGroups || []).find((x) => x.key === key);
                      updComp(i, {
                        group_key: key,
                        group_mode: g?.mode || "exclusive",
                        group_max: g?.mode === "max" ? (g?.max || 2) : null,
                      });
                    } else {
                      updComp(i, { group_key: null, group_mode: null, group_max: null });
                    }
                  }}
                  className="aj-input py-1.5 text-xs flex-1"
                  data-testid={`adv-comp-group-select-${i}`}
                >
                  <option value="">— Sem grupo —</option>
                  {(availableGroups || []).map((g) => (
                    <option key={g.key} value={g.key}>
                      {g.key} · {g.mode === "exclusive" ? "escolha 1" : `máx ${g.max}`}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CaucaoTab({ form, upd, price, caucaoCap, unitWord }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="adv-caucao-section">
      <div className="text-xs font-black text-slate-800 mb-1">Caução (valor retido no cartão do cliente e devolvido ao final)</div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!form.caucao_active}
          onChange={(e) => upd({ caucao_active: e.target.checked, ...(e.target.checked ? {} : { caucao_accept: false }) })}
          className="w-4 h-4 accent-[#FF6B35]" data-testid="adv-caucao-active" />
        <span className="text-xs font-bold text-slate-700">Exigir caução para este item</span>
      </label>
      {form.caucao_active && (
        <div className="space-y-2" data-testid="adv-caucao-block">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Tipo</span>
              <select value={form.caucao_type} onChange={(e) => upd({ caucao_type: e.target.value })} className="aj-input mt-1" data-testid="adv-caucao-type">
                <option value="fixed">Valor fixo (R$)</option>
                <option value="percent">% do valor da locação</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{form.caucao_type === "percent" ? "Percentual (%)" : "Valor (R$)"}</span>
              <input type="number" step="0.01" min="0" placeholder={form.caucao_type === "percent" ? "50" : "100.00"} value={form.caucao_value}
                onChange={(e) => upd({ caucao_value: e.target.value })} className="aj-input mt-1" data-testid="adv-caucao-value" />
            </label>
          </div>
          {price > 0 && (
            <div className="text-[10px] text-slate-500">
              Teto permitido: <b>{formatBRL(caucaoCap)}</b> (10× o valor do anúncio por {unitWord}).
            </div>
          )}
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-2.5 space-y-1.5" data-testid="adv-caucao-warning">
            <div className="text-[11px] font-black text-amber-900 flex items-center gap-1">
              <Warning size={13} weight="fill" /> Importante — leia antes de ativar
            </div>
            <ul className="text-[10px] text-amber-900/90 space-y-1 list-disc pl-4">
              <li>Itens com caução recebem pagamento <b>SOMENTE via cartão de crédito</b> (a caução fica reservada no limite, sem cobrança). PIX e Saldo ficam bloqueados.</li>
              <li>A caução <b>reduz a conversão</b> — use só quando necessário.</li>
              <li>Taxa fixa de <b>{formatBRL(CAUCAO_FEE)}</b> descontada do valor recebido pelo dono em <b>cada locação com caução</b>, independentemente de a caução ser cobrada — é pelo serviço de gerenciamento da retenção.</li>
            </ul>
            <label className="flex items-start gap-2 cursor-pointer pt-1">
              <input type="checkbox" checked={!!form.caucao_accept} onChange={(e) => upd({ caucao_accept: e.target.checked })}
                className="w-4 h-4 accent-amber-600 mt-0.5" data-testid="adv-caucao-accept" />
              <span className="text-[11px] font-bold text-amber-900">
                Entendi e aceito: este item só receberá pagamentos via cartão de crédito.
              </span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function CombosTab({ combos, upd, listings, form }) {
  const setCombo = (i, patch) => upd({ combos: combos.map((x, xi) => (xi === i ? { ...x, ...patch } : x)) });
  const removeCombo = (i) => upd({ combos: combos.filter((_, xi) => xi !== i) });
  const mode = form.combo_upsell_mode || "per_item";
  const stack = !!form.combo_stack_percentages;
  const bundlePct = form.combo_bundle_discount_pct ?? "";
  const progressive = form.combo_progressive || [];

  // helpers p/ escala progressiva
  const addTier = () => upd({ combo_progressive: [...progressive, { qty_extras: progressive.length + 1, discount_pct: "" }] });
  const setTier = (i, patch) => upd({ combo_progressive: progressive.map((t, ti) => ti === i ? { ...t, ...patch } : t) });
  const rmTier = (i) => upd({ combo_progressive: progressive.filter((_, ti) => ti !== i) });

  const MODE_LABEL = {
    per_item: "Por item extra",
    total_bundle: "Um único % no kit inteiro",
    progressive: "Escala progressiva (mais extras = mais desconto)",
  };

  return (
    <div className="space-y-2" data-testid="adv-combos-section">
      <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-2.5 text-[11px] text-emerald-900">
        <b>Combos / Upsell:</b> ofereça <b>outros anúncios seus</b> como extras — o cliente completa
        o kit no mesmo período com desconto. Combos sem estoque na data são ocultados automaticamente na tela do cliente.
      </div>

      {/* V17.9.2 — Modo de desconto (nível do anúncio) */}
      <div className="rounded-xl border-2 border-slate-200 bg-white p-3 space-y-2.5" data-testid="adv-combo-mode-block">
        <div>
          <span className="text-[10px] uppercase font-black tracking-wider text-slate-600">Modo do desconto</span>
          <div className="grid grid-cols-1 gap-1.5 mt-1.5">
            {[
              { v: "per_item", label: "Por item extra", hint: "Cada item combo tem seu próprio % — o dono decide qual." },
              { v: "total_bundle", label: "Único % no kit", hint: "Um único desconto vale para todos os combos adicionados." },
              { v: "progressive", label: "Escala progressiva", hint: "% cresce conforme o cliente adiciona mais extras (1 extra: X%, 2: Y%, 3+: Z%)." },
            ].map((m) => {
              const active = mode === m.v;
              return (
                <button key={m.v} type="button"
                  onClick={() => upd({ combo_upsell_mode: m.v })}
                  className={`text-left rounded-lg border-2 p-2.5 transition ${active ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-emerald-300"}`}
                  data-testid={`adv-upsell-mode-${m.v}`}>
                  <div className="flex items-start gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 shrink-0 mt-0.5 ${active ? "border-emerald-500 bg-emerald-500" : "border-slate-300"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-black text-slate-800">{m.label}</div>
                      <div className="text-[10px] text-slate-500">{m.hint}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {mode === "per_item" && (
          <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-slate-200 bg-slate-50 p-2 mt-2" data-testid="adv-upsell-stack-toggle">
            <input type="checkbox" checked={stack} onChange={(e) => upd({ combo_stack_percentages: e.target.checked })}
              className="w-4 h-4 accent-emerald-600 mt-0.5" data-testid="adv-upsell-stack" />
            <span className="flex-1">
              <span className="text-xs font-bold text-slate-800">Somar % quando o cliente marca vários combos</span>
              <span className="block text-[10px] text-slate-500 mt-0.5">
                Ex.: combo A=10%, combo B=15%. Marcou só A → 10%. Marcou A+B → <b>25% em cada extra do kit</b>.
              </span>
            </span>
          </label>
        )}

        {mode === "total_bundle" && (
          <label className="block mt-2">
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
              % de desconto para cada extra do kit
            </span>
            <input type="number" step="1" min="0" max="90" placeholder="20"
              value={bundlePct} onChange={(e) => upd({ combo_bundle_discount_pct: e.target.value })}
              className="aj-input mt-1" data-testid="adv-upsell-bundle-pct" />
            <span className="text-[10px] text-slate-400 block mt-0.5">
              Vale para <b>todos</b> os combos adicionados ao kit. Item principal fica com preço cheio.
            </span>
          </label>
        )}

        {mode === "progressive" && (
          <div className="mt-2 space-y-1.5" data-testid="adv-upsell-progressive-block">
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block">Faixas de desconto por qtd de EXTRAS</span>
            {progressive.map((t, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2" data-testid={`adv-upsell-tier-${i}`}>
                <span className="text-[10px] text-slate-500">A partir de</span>
                <input type="number" min="1" step="1" value={t.qty_extras || ""}
                  onChange={(e) => setTier(i, { qty_extras: e.target.value })}
                  className="aj-input py-1 px-2 text-xs w-14 text-center" data-testid={`adv-upsell-tier-qty-${i}`} />
                <span className="text-[10px] text-slate-500">extra(s):</span>
                <input type="number" min="0" max="90" step="1" value={t.discount_pct || ""}
                  onChange={(e) => setTier(i, { discount_pct: e.target.value })}
                  className="aj-input py-1 px-2 text-xs w-16 text-center" data-testid={`adv-upsell-tier-pct-${i}`} />
                <span className="text-[10px] text-slate-500">% off</span>
                <button type="button" onClick={() => rmTier(i)} className="ml-auto text-red-500 hover:text-red-700" data-testid={`adv-upsell-tier-remove-${i}`}>
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            ))}
            <button type="button" onClick={addTier}
              className="text-[11px] font-bold text-emerald-700 border-2 border-emerald-200 rounded-full px-3 py-1.5 hover:bg-emerald-50 inline-flex items-center gap-1"
              data-testid="adv-upsell-tier-add">
              <Plus size={12} weight="bold" /> Adicionar faixa
            </button>
            {progressive.length === 0 && (
              <div className="text-[10px] text-slate-400">Ex.: 1 extra = 10%; 2 extras = 15%; 3+ extras = 20%.</div>
            )}
          </div>
        )}
      </div>

      {/* Lista de combos */}
      {combos.map((cb, i) => (
        <div key={i} className="rounded-xl border-2 border-slate-200 bg-white p-3 space-y-2.5" data-testid={`adv-combo-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-black tracking-wider text-emerald-700">Combo {i + 1}</span>
            <button type="button" onClick={() => removeCombo(i)} className="text-red-500 hover:text-red-700" data-testid={`adv-combo-remove-${i}`}>
              <Trash size={14} weight="bold" />
            </button>
          </div>

          <div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-1">Item combo</span>
            <ListingThumbSelect
              value={cb.listing_id}
              onChange={(v) => setCombo(i, { listing_id: v })}
              listings={listings.filter((l) => !l._used || String(l.id) === String(cb.listing_id))}
              placeholder="Selecione outro anúncio seu…"
              testid={`adv-combo-listing-${i}`}
            />
          </div>

          {mode === "per_item" && (
            <div className="space-y-1.5">
              <label className="block">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
                  Se ESSE item for adicionado como extra, desconto de:
                </span>
                <div className="relative mt-1">
                  <input type="number" step="1" min="0" max="90" placeholder="15" value={cb.discount_pct}
                    onChange={(e) => setCombo(i, { discount_pct: e.target.value })}
                    className="aj-input pr-8" data-testid={`adv-combo-discount-${i}`} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">%</span>
                </div>
              </label>
              {stack && (
                <div className="text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-1.5">
                  <b>Stack ativo:</b> se o cliente marcar este e outros combos, os % se somam e aplicam a cada extra.
                </div>
              )}
            </div>
          )}

          <label className="block">
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Frase (opcional)</span>
            <input placeholder="Leve junto e economize!" value={cb.note}
              onChange={(e) => setCombo(i, { note: e.target.value })}
              className="aj-input mt-1" data-testid={`adv-combo-note-${i}`} />
          </label>
        </div>
      ))}

      {combos.length < 4 && (
        <button type="button" onClick={() => upd({ combos: [...combos, { listing_id: "", discount_pct: "", note: "" }] })}
          className="text-[11px] font-bold text-emerald-700 inline-flex items-center gap-1 px-3 py-2 border-2 border-emerald-300 rounded-full w-full justify-center hover:bg-emerald-50"
          data-testid="adv-add-combo">
          <Plus size={12} weight="bold" /> Adicionar combo (máx. 4)
        </button>
      )}

      {listings.length === 0 && (
        <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
          Você precisa ter outros anúncios ativos para poder criar combos.
        </div>
      )}

      {/* Resumo visual da configuração escolhida */}
      {mode !== "per_item" && combos.length > 0 && (
        <div className="rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50/40 p-2.5 text-[11px] text-emerald-900" data-testid="adv-upsell-summary">
          <b>Como o cliente verá:</b>{" "}
          {mode === "total_bundle" && `Todos os ${combos.length} combos com um único desconto de ${bundlePct || 0}% em cada extra.`}
          {mode === "progressive" && progressive.length > 0 && (
            <>Faixas: {progressive.map((t) => `${t.qty_extras}+ extras → ${t.discount_pct}%`).join(" · ")}.</>
          )}
        </div>
      )}
    </div>
  );
}
